import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { can } from "@/lib/permissions";
import { StatusBadge, ColorDot } from "@/components/StatusBadge";
import { EvidenceChain, EvidenceNotePane } from "@/components/EvidenceAssuranceGraph";
import { ReadinessRail } from "@/components/ReadinessRail";
import { Panel, PanelHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  MessagesSquare, UserCheck, RefreshCw, ShieldCheck, History, ArrowLeft, FilePlus,
  CheckCircle2, CircleDot, Truck, ShieldAlert, ReceiptText, ListChecks, FileDown,
  Eye, Rewind, ClipboardList, Scale, ArrowLeftRight, FolderSearch, FlaskConical, Minus,
} from "lucide-react";

function downloadPdf(url) {
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const FACT_LABELS = {
  respiratory_symptom: "Respiratory symptom", symptom_duration: "Symptom duration",
  clinical_indication: "Clinical indication", management_impact: "Management impact",
};
const CHECK_CLS = {
  Satisfied: "text-emerald-700", Missing: "text-amber-700", "Review Required": "text-slate-600",
  "Not Applicable": "text-slate-400", "Future Integration": "text-sky-600", "To Be Confirmed": "text-amber-700",
  Simulated: "text-sky-600",
};

export default function OrderResult() {
  const { id } = useParams();
  const nav = useNavigate();
  const { roleCode, shadowLabel, operatingMode } = useApp();
  const [o, setO] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [prebill, setPrebill] = useState(null);
  const [busy, setBusy] = useState(false);
  const [clarOpen, setClarOpen] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [clarMsg, setClarMsg] = useState("");
  const [docText, setDocText] = useState("");
  const [reviewReason, setReviewReason] = useState("");
  const [releaseResult, setReleaseResult] = useState(null);
  const [pa, setPa] = useState({ status: "Not Evaluated", owner: "To Be Confirmed" });
  const [abn, setAbn] = useState({ status: "Not Evaluated", owner: "To Be Confirmed" });
  const [outcome, setOutcome] = useState({ status: "Paid", amount: "0", denial_reason: "" });
  const [shadow, setShadow] = useState({ human_decision: "", agreement: "Pending", claim_outcome: "", reviewer_notes: "" });
  const [shadowLog, setShadowLog] = useState([]);
  const [replay, setReplay] = useState(null);
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState("requirements");

  const load = useCallback(async () => {
    const data = await api.order(id);
    setO(data);
    if (data.prior_auth) setPa(data.prior_auth);
    if (data.abn) setAbn(data.abn);
    api.evidenceChecklist(id).then(setChecklist).catch(() => {});
    api.decisionReplay(id).then(setReplay).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!o) return <div className="text-sm text-slate-500">Loading order…</div>;

  const ev = o.latest_evaluation;
  const note = o.documents?.length ? o.documents[o.documents.length - 1].text : "";
  const verifiedSpans = (ev?.facts || []).filter((f) => f.span_verified);
  const missing = (ev?.criterion_results || []).filter((r) => r.status !== "SATISFIED");
  const color = o.readiness_color;
  const readinessStatus = o.risk_flag ? "RED" : (ev?.status || o.status);
  const workflowChip = WORKFLOW_LABELS[o.status];

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const reEval = () => act(() => api.evaluate(id, { role_code: roleCode }), "Re-evaluation complete");
  const genPacket = () => act(async () => {
    const p = await api.generatePacket(id, { role_code: roleCode });
    setTimeout(() => nav(`/defense-packets/${p.id}`), 400);
  }, "Evidence Packet generated");
  const exportPdf = () => {
    downloadPdf(api.exportPacketPdfUrl(id, roleCode));
    toast.success("Evidence Packet PDF exported");
  };
  const submitClar = () => act(async () => {
    await api.clarify(id, { role_code: roleCode, message: clarMsg || undefined });
    setClarOpen(false); setClarMsg("");
  }, "Clarification request sent");
  const submitDoc = () => act(async () => {
    await api.addDocument(id, { role_code: roleCode, text: docText, label: "Supplemental documentation", append: true });
    setDocOpen(false); setDocText("");
    await api.evaluate(id, { role_code: roleCode });
  }, "Supplemental documentation added and re-evaluated");
  const submitReview = () => act(async () => {
    await api.routeReview(id, { role_code: roleCode, reason: reviewReason || "Manually routed to human review." });
    setReviewOpen(false); setReviewReason("");
  }, "Routed to human review");

  const doRelease = async () => {
    setBusy(true);
    try { const r = await api.release(id, { role_code: roleCode }); setReleaseResult(r); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Release failed"); }
    finally { setBusy(false); }
  };
  const toggleRisk = (flag) => act(() => api.markRisk(id, { role_code: roleCode, risk: flag }), flag ? "High-risk (RED) flag set" : "High-risk flag cleared");
  const savePa = () => act(() => api.priorAuth(id, { role_code: roleCode, ...pa }), "Prior authorization updated");
  const saveAbn = () => act(() => api.abn(id, { role_code: roleCode, ...abn }), "ABN status updated");
  const runPrebill = async () => {
    setBusy(true);
    try { const r = await api.prebilling(id, { role_code: roleCode }); setPrebill(r); }
    catch (e) { toast.error("Prebilling failed"); }
    finally { setBusy(false); }
  };
  const recordOutcome = () => act(() => api.outcome(id, { role_code: roleCode, ...outcome, amount: Number(outcome.amount) || 0 }), "Synthetic outcome recorded");
  const recordShadow = async () => {
    setBusy(true);
    try {
      const obs = await api.shadowObservation(id, { role_code: roleCode, ...shadow });
      setShadowLog((l) => [obs, ...l]);
      setShadow({ human_decision: "", agreement: "Pending", claim_outcome: "", reviewer_notes: "" });
      toast.success("Shadow observation recorded (validation only)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to record observation"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <Button variant="ghost" size="sm" className="mb-3 text-slate-500" onClick={() => nav("/orders")} data-testid="back-orders">
        <ArrowLeft className="mr-1 h-4 w-4" /> Orders
      </Button>

      {/* Header */}
      <Panel className="mb-4 p-5" data-testid="order-result-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="border-l-2 border-teal-600 pl-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-teal-700">Order Workspace</div>
            <div className="mt-0.5 flex items-center gap-3">
              <h1 className="font-display text-xl font-bold text-slate-900">{o.patient_name}</h1>
              <StatusBadge status={readinessStatus} />
              {workflowChip && <span className="rounded border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500" data-testid="workflow-chip">{workflowChip}</span>}
              {o.overridden && <span className="rounded bg-slate-800 px-2 py-0.5 text-[10px] font-semibold text-white">OVERRIDDEN</span>}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600">
              <div><span className="text-slate-400">Order</span> <span className="font-mono">{o.order_number}</span></div>
              <div><span className="text-slate-400">Test</span> {o.test_name}</div>
              <div><span className="text-slate-400">Payer</span> {o.payer_name}</div>
              <div><span className="text-slate-400">Policy context</span> {ev?.policy_id ? `${ev.policy_id} · synthetic (not CMS/Novitas)` : "No applicable policy configured"}</div>
            </div>
          </div>
        </div>
      </Panel>

      {/* Readiness Rail — pinned across the top of the workspace */}
      <div className="mb-4"><ReadinessRail order={o} /></div>

      {ev?.status === "READY" && !o.risk_flag && (
        <div className="mb-4 rounded-md border-l-4 border-l-emerald-500 border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800">
          No configured reimbursement-readiness issue was identified within this demonstration rule set.
          <strong> This is not a guarantee of coverage or payment.</strong>
        </div>
      )}

      {/* Purpose-built 3-pane working environment */}
      <div className="mb-5 grid grid-cols-1 gap-4 xl:grid-cols-[300px_minmax(0,1fr)_340px]">
        {/* LEFT — source clinical / order record */}
        <div className="space-y-4">
          <StageKicker n="1" title="Source Record" rail="ORDER" />
          <Panel data-testid="source-record">
            <PanelHeader label="Source / Order Record" icon={ClipboardList} />
            <dl className="space-y-1.5 p-4 text-xs text-slate-600">
              <Row k="Patient (synthetic)" v={o.patient_name} />
              <Row k="Patient ID" v={o.synthetic_patient_id} />
              <Row k="Ordering provider" v={o.provider_name} />
              <Row k="Test" v={o.test_name} />
              <Row k="Payer context" v={o.payer_name} />
              <Row k="Documents on file" v={`${(o.documents || []).length} version(s)`} />
            </dl>
          </Panel>
          <EvidenceNotePane note={note || ""} spans={verifiedSpans} active={active} />
          <p className="text-[11px] text-slate-500">
            <strong>View in Note</strong> highlights the exact source text. Highlights appear only where a
            verbatim source span was verified — RequiSure never fabricates highlighted evidence.
          </p>
        </div>

        {/* CENTER — evidence-to-requirement mapping */}
        <div>
          <StageKicker n="2" title="Evidence & Coverage" rail="EVIDENCE · COVERAGE" />
          {ev?.policy_id && (
            <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600" data-testid="applied-policy-strip">
              <Scale className="h-3.5 w-3.5 text-slate-400" />
              <span className="font-semibold text-slate-700">Applied coverage policy</span>
              <span className="font-mono text-slate-500">{ev.policy_id} · v{ev.policy_version}</span>
              <span className="text-slate-400">eff. {ev.policy_effective_date}</span>
              <span className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[9px] font-semibold uppercase text-slate-500">Synthetic · not CMS</span>
            </div>
          )}
          <EvidenceChain evaluation={ev} active={active} setActive={setActive} />
        </div>

        {/* RIGHT — readiness decision, actions, ownership */}
        <div className="space-y-4">
          <StageKicker n="3" title="Decision & Action" rail="ACTION · RELEASE" />
          <Panel data-testid="readiness-summary">
            <PanelHeader label="Readiness Decision" right={<ColorDot color={color} label={color} />} />
            <div className="p-4">
              <StatusBadge status={readinessStatus} />
              <div className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Why</div>
              <div className="text-sm text-slate-700">{ev?.summary || "Not yet evaluated."}</div>
              <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-600">
                <UserCheck className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-slate-400">Owner:</span>
                <span className="font-medium text-slate-700">{o.risk_flag ? "Revenue / Compliance reviewer" : (OWNER_BY_STATUS[o.status] || "To be confirmed")}</span>
              </div>
              {ev && !ev.unsupported && ev.required_total > 0 && (
                <div className="mt-2" data-testid="required-criteria-count"
                  title="All applicable mandatory criteria satisfied means the configured required checks are complete based on the information RequiSure had available — it is not a percentage, score, or guarantee of payment.">
                  <span className="font-display text-lg font-bold text-slate-900">{ev.required_satisfied} / {ev.required_total} Required Criteria Satisfied</span>
                  <div className="text-[10px] text-slate-400">Configured mandatory checks complete — not a guarantee of payment.</div>
                </div>
              )}
              {missing.length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-2">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Unresolved</div>
                  <ul className="space-y-1">
                    {missing.map((r) => (
                      <li key={r.criterion_code} className="flex items-start gap-1.5 text-xs text-slate-600">
                        <CircleDot className="mt-0.5 h-3 w-3 shrink-0 text-amber-600" />
                        <span><strong>Criterion {r.criterion_code}:</strong> {r.next_action || r.reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Panel>

          {((o.reviews || []).some((r) => r.state === "OPEN") || operatingMode === "SHADOW") && (
            <Panel data-testid="workflow-status">
              <PanelHeader label="Workflow" />
              <div className="space-y-2 p-4">
                {(o.reviews || []).some((r) => r.state === "OPEN") && (
                  <button className="flex w-full items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 transition-colors hover:bg-amber-100" data-testid="jump-review" onClick={() => setTab("review")}>
                    <span className="flex items-center gap-1.5"><UserCheck className="h-3.5 w-3.5" /> Open human review</span>
                    <span className="font-semibold">{(o.reviews || []).filter((r) => r.state === "OPEN").length} &rarr;</span>
                  </button>
                )}
                {operatingMode === "SHADOW" && (
                  <button className="flex w-full items-center justify-between rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800 transition-colors hover:bg-indigo-100" data-testid="jump-shadow" onClick={() => setTab("shadow")}>
                    <span className="flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" /> Shadow Mode comparison</span>
                    <span className="font-semibold">Open &rarr;</span>
                  </button>
                )}
              </div>
            </Panel>
          )}

          <Panel data-testid="action-palette">
            <PanelHeader label="Actions" />
            <div className="flex flex-col gap-2 p-4">
              {can.clarify(roleCode) && <Button size="sm" variant="outline" className="justify-start" data-testid="action-clarify" onClick={() => setClarOpen(true)}><MessagesSquare className="mr-1.5 h-4 w-4" /> Request Clarification</Button>}
              {can.route(roleCode) && <Button size="sm" variant="outline" className="justify-start" data-testid="action-route-review" onClick={() => setReviewOpen(true)}><UserCheck className="mr-1.5 h-4 w-4" /> Route to Human Review</Button>}
              {can.respondClarify(roleCode) && <Button size="sm" variant="outline" className="justify-start" data-testid="action-add-doc" onClick={() => setDocOpen(true)}><FilePlus className="mr-1.5 h-4 w-4" /> Add Documentation</Button>}
              {can.evaluate(roleCode) && <Button size="sm" variant="outline" className="justify-start" data-testid="action-reeval" disabled={busy} onClick={reEval}><RefreshCw className="mr-1.5 h-4 w-4" /> Re-evaluate</Button>}
              <Button size="sm" className="justify-start bg-teal-600 hover:bg-teal-700" data-testid="action-gen-packet" disabled={busy} onClick={genPacket}><ShieldCheck className="mr-1.5 h-4 w-4" /> Generate Evidence Packet</Button>
              <Button size="sm" variant="outline" className="justify-start border-teal-600 text-teal-700 hover:bg-teal-50" data-testid="export-packet-pdf" onClick={exportPdf}><FileDown className="mr-1.5 h-4 w-4" /> Export Evidence Packet (PDF)</Button>
            </div>
          </Panel>

          <Panel data-testid="ownership-quick">
            <PanelHeader label="Release & Ownership" />
            <dl className="space-y-1.5 p-4 text-xs text-slate-600">
              <Row k="Order release" v={o.released_simulated ? "Released — SIMULATED" : "Not released"} />
              <Row k="Prior authorization" v={`${pa.status} · ${pa.owner}`} />
              <Row k="ABN" v={`${abn.status} · ${abn.owner}`} />
              <Row k="High-risk flag" v={o.risk_flag ? "RED — REQUIRES VALIDATION" : "None"} />
            </dl>
          </Panel>
        </div>
      </div>

      {/* v0.2 — Order vs. Clinical Record Reconciliation */}
      {ev && !ev.unsupported && (ev.reconciliation || []).length > 0 && (
        <Panel className="mb-4" data-testid="reconciliation-panel">
          <PanelHeader label="Order vs. Clinical Record Reconciliation" icon={ArrowLeftRight} />
          <div className="p-4">
            <p className="mb-3 text-xs text-slate-500">Compares what the order / requisition asserts against what the available clinical record supports. RequiSure surfaces mismatches — it never rewrites the order or the record.</p>
            <div className="overflow-hidden rounded-md border border-slate-200">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Item</th>
                    <th className="px-3 py-2 font-semibold">On the order</th>
                    <th className="px-3 py-2 font-semibold">In the available record</th>
                    <th className="px-3 py-2 font-semibold">Reconciliation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {ev.reconciliation.map((r, i) => (
                    <tr key={i} data-testid={`recon-row-${i}`} className="align-top">
                      <td className="px-3 py-2 font-medium text-slate-700">{r.item}</td>
                      <td className="px-3 py-2 text-slate-600">{r.order_value || "— (not asserted on order)"}</td>
                      <td className="px-3 py-2 text-slate-600">{r.record_value}</td>
                      <td className="px-3 py-2"><span className={reconPill(r.status)}>{r.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Panel>
      )}

      {/* v0.2 — Testing History + Source Coverage */}
      <div className="mb-5 grid gap-4 lg:grid-cols-2">
        <Panel data-testid="testing-history-panel">
          <PanelHeader label="Testing History" icon={History} />
          <div className="p-4 text-xs text-slate-600"><TestingHistory th={o.testing_history} /></div>
        </Panel>
        <Panel data-testid="source-coverage-panel">
          <PanelHeader label="Source Coverage" icon={FolderSearch} />
          <div className="p-4 text-xs text-slate-600"><SourceCoverage sc={o.source_coverage} /></div>
        </Panel>
      </div>

      {/* Deep-dive detail */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="requirements" data-testid="tab-requirements">Requirements & Actions</TabsTrigger>
          <TabsTrigger value="checklist" data-testid="tab-checklist">Evidence Checklist</TabsTrigger>
          <TabsTrigger value="provenance" data-testid="tab-provenance">Provenance & Extraction</TabsTrigger>
          <TabsTrigger value="release" data-testid="tab-release">Release & Billing</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="review" data-testid="tab-review">Human Review</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Timeline</TabsTrigger>
          <TabsTrigger value="shadow" data-testid="tab-shadow">Shadow Mode</TabsTrigger>
          <TabsTrigger value="replay" data-testid="tab-replay">Decision Replay</TabsTrigger>
        </TabsList>

        <TabsContent value="requirements">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-4">
              <div className="mb-3 text-sm font-semibold text-slate-800">Missing / Unresolved Requirements</div>
              {missing.length === 0 ? (
                <div className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" /> All criteria satisfied.</div>
              ) : missing.map((r) => (
                <div key={r.criterion_code} className="mb-2 rounded-md border border-slate-200 p-3" data-testid={`missing-${r.criterion_code}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-800">Criterion {r.criterion_code}</span>
                    <StatusBadge status={r.status} size="sm" />
                  </div>
                  <div className="mt-1 text-xs text-slate-600">{r.description}</div>
                  <div className="mt-1 text-xs font-medium text-amber-800">{r.reason}</div>
                </div>
              ))}
            </Card>
            <Card className="p-4">
              <div className="mb-3 text-sm font-semibold text-slate-800">Recommended Operational Action</div>
              {missing.filter((r) => r.next_action).length === 0 ? (
                <div className="text-sm text-slate-500">No operational action required.</div>
              ) : missing.filter((r) => r.next_action).map((r) => (
                <div key={r.criterion_code} className="mb-2 flex items-start gap-2 text-sm text-slate-700">
                  <CircleDot className="mt-0.5 h-4 w-4 text-teal-600" />
                  <span><strong>Criterion {r.criterion_code}:</strong> {r.next_action}</span>
                </div>
              ))}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="checklist">
          <Card className="p-4" data-testid="evidence-checklist">
            <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800"><ListChecks className="h-4 w-4" /> Electronic Evidence Checklist</div>
            <p className="mb-3 text-xs text-slate-500">Clinical-condition items are wired to the demonstration rule evaluation; other items show honest status labels (Future Integration / To Be Confirmed).</p>
            <div className="grid gap-3 md:grid-cols-2">
              {checklist?.sections?.map((sec) => (
                <div key={sec.section} className="rounded-md border border-slate-200 p-3">
                  <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">{sec.section}</div>
                  <div className="space-y-1">
                    {sec.items.map((it, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-slate-700">{it.label}{it.traceable && <span className="ml-1 text-[10px] text-teal-600">· source-traceable</span>}</span>
                        <span className={`font-semibold ${CHECK_CLS[it.status] || "text-slate-500"}`}>{it.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="provenance">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-4" data-testid="policy-provenance">
              <div className="mb-3 text-sm font-semibold text-slate-800">Demonstration Policy Provenance</div>
              {ev?.policy_id ? (
                <dl className="space-y-1.5 text-xs text-slate-700">
                  <Row k="Policy" v={ev.policy_id} /><Row k="Version" v={ev.policy_version} />
                  <Row k="Effective date" v={ev.policy_effective_date} />
                  <Row k="Source" v="Synthetic demonstration content — not CMS" />
                  <Row k="Payer context" v={o.payer_name} /><Row k="MAC" v="Novitas — Jurisdiction H / Texas (demonstration)" />
                </dl>
              ) : <div className="text-sm text-slate-500">No approved applicable policy is configured for this payer / test scope. RequiSure does not borrow another payer's rule.</div>}
              {ev?.policy_id && <div className="mt-3 rounded bg-slate-50 p-2 text-[11px] text-slate-500">Synthetic policy criterion for prototype validation only. Specific Novitas LCD/Article: To Be Confirmed. Does not represent actual CMS coverage policy.</div>}
            </Card>
            <Card className="p-4" data-testid="ai-extraction-summary">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-800">AI Extraction Summary</div>
                <span className="rounded border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-600">{ev?.extraction_source === "demo" ? "Demo extraction (fallback)" : "LLM extraction"}</span>
              </div>
              {(ev?.facts || []).length === 0 ? (
                <div className="text-sm text-slate-500">No verifiable facts were extracted from the submitted documentation.</div>
              ) : (
                <div className="space-y-2">
                  {ev.facts.map((fct, i) => (
                    <div key={i} className="rounded-md border border-slate-200 p-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-slate-700">{FACT_LABELS[fct.fact_type] || fct.fact_type}</span>
                        <span className="text-slate-500">conf {Math.round(fct.confidence * 100)}%{fct.ambiguous && " · ambiguous"}</span>
                      </div>
                      <div className="italic text-slate-600">“{fct.source_quote}”</div>
                      <div className="text-[10px] text-slate-400">→ {fct.normalized_value} · span [{fct.character_start}-{fct.character_end}] verified</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="release">
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Hold / release */}
            <Card className="p-4" data-testid="release-control">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800"><Truck className="h-4 w-4" /> Controlled Hold / Release</div>
              <div className="mb-3"><ColorDot color={color} label={`Current readiness: ${color}`} /></div>
              {releaseResult && (
                <div className={`mb-3 rounded-md border p-2.5 text-xs ${releaseResult.release === "RELEASED_SIMULATED" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : releaseResult.color === "RED" ? "border-rose-200 bg-rose-50 text-rose-800" : "border-amber-200 bg-amber-50 text-amber-900"}`} data-testid="release-result">
                  <div className="font-semibold">{releaseResult.release === "RELEASED_SIMULATED" ? "Eligible — Simulated Release" : releaseResult.release === "BLOCKED" ? "Routine Release Blocked" : "Order Held"}</div>
                  <div>{releaseResult.reason}</div>
                </div>
              )}
              {can.release(roleCode) && (
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800" data-testid="release-btn" disabled={busy} onClick={doRelease}>
                  <Truck className="mr-1.5 h-4 w-4" /> Release to LIS — SIMULATED
                </Button>
              )}
              {can.markRisk(roleCode) && (
                <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
                  <ShieldAlert className="h-4 w-4 text-rose-500" />
                  <span className="text-xs text-slate-600">High-risk demonstration flag (RED) — <em>REQUIRES VALIDATION</em>:</span>
                  {o.risk_flag
                    ? <Button size="sm" variant="outline" data-testid="clear-risk-btn" disabled={busy} onClick={() => toggleRisk(false)}>Clear RED</Button>
                    : <Button size="sm" variant="outline" className="border-rose-300 text-rose-700" data-testid="mark-risk-btn" disabled={busy} onClick={() => toggleRisk(true)}>Mark RED</Button>}
                </div>
              )}
            </Card>

            {/* Prior auth + ABN */}
            <Card className="p-4" data-testid="auth-control">
              <div className="mb-2 text-sm font-semibold text-slate-800">Prior Authorization & ABN</div>
              <div className="mb-2 rounded bg-amber-50 p-2 text-[11px] text-amber-800">Prototype workflow only — controlling payer/compliance requirements must be validated before use.</div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Prior Authorization</Label>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <Select value={pa.status} onValueChange={(v) => setPa({ ...pa, status: v })}>
                      <SelectTrigger className="h-8 w-56 text-xs" data-testid="pa-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{["Not Evaluated", "Required — Simulated", "Not Required — Demo Rule", "Pending", "Approved — Synthetic", "Denied — Synthetic", "To Be Confirmed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input className="h-8 w-40 text-xs" data-testid="pa-owner" placeholder="Owner (TBC)" value={pa.owner} onChange={(e) => setPa({ ...pa, owner: e.target.value })} />
                    <Button size="sm" variant="outline" data-testid="pa-save" disabled={busy} onClick={savePa}>Save</Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">ABN</Label>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <Select value={abn.status} onValueChange={(v) => setAbn({ ...abn, status: v })}>
                      <SelectTrigger className="h-8 w-56 text-xs" data-testid="abn-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{["Not Evaluated", "Potentially Required", "Not Required", "To Be Confirmed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input className="h-8 w-40 text-xs" data-testid="abn-owner" placeholder="Owner (TBC)" value={abn.owner} onChange={(e) => setAbn({ ...abn, owner: e.target.value })} />
                    <Button size="sm" variant="outline" data-testid="abn-save" disabled={busy} onClick={saveAbn}>Save</Button>
                  </div>
                </div>
              </div>
            </Card>

            {/* Prebilling */}
            <Card className="p-4" data-testid="prebilling-control">
              <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800"><ReceiptText className="h-4 w-4" /> Final Prebilling Verification</div>
              <div className="mb-2 rounded bg-sky-50 p-2 text-[11px] font-semibold text-sky-700">SIMULATED — NO BILLING SYSTEM CONNECTED</div>
              <Button size="sm" variant="outline" data-testid="prebill-run" disabled={busy} onClick={runPrebill}>Run Prebilling Verification</Button>
              {prebill && (
                <div className="mt-3">
                  <div className="mb-2 text-xs font-semibold text-slate-700">Overall: {prebill.overall}</div>
                  <div className="grid grid-cols-2 gap-1">
                    {prebill.checks.map((c) => (
                      <div key={c.check} className="flex items-center justify-between rounded border border-slate-100 px-2 py-1 text-[11px]">
                        <span className="text-slate-600">{c.check}</span>
                        <span className={c.status.startsWith("Ready") ? "text-emerald-600" : "text-amber-700"}>{c.status.startsWith("Ready") ? "OK" : "TBC"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* Outcome */}
            <Card className="p-4" data-testid="outcome-control">
              <div className="mb-1 text-sm font-semibold text-slate-800">Outcome Feedback (Synthetic / Future RCM)</div>
              <div className="mb-2 rounded bg-slate-50 p-2 text-[11px] text-slate-500">All values synthetic. Outcome data never silently modifies approved payer rules.</div>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={outcome.status} onValueChange={(v) => setOutcome({ ...outcome, status: v })}>
                  <SelectTrigger className="h-8 w-44 text-xs" data-testid="outcome-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Paid", "Denied", "Adjusted", "Appealed", "Recovered", "Written Off", "Patient Responsibility"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Input className="h-8 w-28 text-xs" data-testid="outcome-amount" placeholder="Amount" value={outcome.amount} onChange={(e) => setOutcome({ ...outcome, amount: e.target.value })} />
                <Input className="h-8 w-48 text-xs" data-testid="outcome-reason" placeholder="Denial reason (optional)" value={outcome.denial_reason} onChange={(e) => setOutcome({ ...outcome, denial_reason: e.target.value })} />
                <Button size="sm" variant="outline" data-testid="outcome-save" disabled={busy} onClick={recordOutcome}>Record</Button>
              </div>
              {o.outcome && <div className="mt-2 text-xs text-slate-600">Recorded: <strong>{o.outcome.status}</strong> · {o.outcome.amount} · {o.outcome.outcome_date}</div>}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="documents">
          <Card className="p-4" data-testid="documents-panel">
            <div className="mb-3 text-sm font-semibold text-slate-800">Versioned Clinical Documents</div>
            <p className="mb-3 text-xs text-slate-500">Documents are versioned and never overwritten. Each evaluation references the exact version used.</p>
            <div className="space-y-3">
              {(o.documents || []).map((d) => (
                <div key={d.id} className="rounded-md border border-slate-200 p-3" data-testid={`doc-v${d.version}`}>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-800">Version {d.version} — {d.label}</span>
                    <span className="font-mono text-[10px] text-slate-400">{new Date(d.created_at).toLocaleString()}</span>
                  </div>
                  <p className="whitespace-pre-wrap text-xs text-slate-600">{d.text || "(empty)"}</p>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="review">
          <Card className="p-4" data-testid="human-review-status">
            <div className="mb-3 text-sm font-semibold text-slate-800">Human Review Status</div>
            {(o.reviews || []).length === 0 && <div className="text-sm text-slate-500">No human review has been opened for this order.</div>}
            {(o.reviews || []).map((r) => (
              <div key={r.id} className="mb-2 rounded-md border border-slate-200 p-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-800">{r.state === "OPEN" ? "Open review" : "Resolved review"}{r.auto_opened && " (auto-opened)"}</span>
                  <span className="text-slate-400">{new Date(r.created_at).toLocaleString()}</span>
                </div>
                <div className="mt-1 text-slate-600">Reason: {r.reason}</div>
                {r.resolution && <div className="mt-1 text-teal-700">Resolution: {r.resolution} — {r.resolved_by}</div>}
              </div>
            ))}
            {(o.overrides || []).map((ov) => (
              <div key={ov.id} className="mb-2 rounded-md border border-slate-300 bg-slate-50 p-3 text-xs">
                <div className="font-semibold text-slate-800">Override: {ov.original_status} → {ov.new_status}</div>
                <div className="text-slate-600">Category: {ov.reason_category}</div>
                <div className="text-slate-600">Justification: {ov.justification}</div>
                <div className="text-slate-400">By {ov.reviewer} · {new Date(ov.created_at).toLocaleString()} · original automated decision retained</div>
              </div>
            ))}
            {(o.clarifications || []).length > 0 && (
              <div className="mt-3">
                <div className="mb-2 text-sm font-semibold text-slate-800">Clarification History</div>
                {o.clarifications.map((c) => (
                  <div key={c.id} className="mb-1.5 rounded-md border border-slate-200 p-2 text-xs">
                    <div className="flex items-center justify-between"><span className="font-semibold text-slate-700">{c.state}</span><span className="text-slate-400">{new Date(c.created_at).toLocaleString()}</span></div>
                    <div className="text-slate-600">{c.message}</div>
                    {c.response && <div className="text-teal-700">Response: {c.response}</div>}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="audit">
          <Card className="p-4" data-testid="audit-timeline">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><History className="h-4 w-4" /> Audit Timeline (append-only, hash-chained)</div>
            <div className="space-y-2">
              {(o.audit || []).map((a) => (
                <div key={a.id} className="flex gap-3 border-l-2 border-slate-200 pl-3 text-xs" data-testid={`audit-${a.seq}`}>
                  <div className="w-32 shrink-0 font-mono text-slate-400">{new Date(a.timestamp).toLocaleTimeString()}</div>
                  <div>
                    <div className="font-semibold text-slate-800">{a.event_type.replace(/_/g, " ")}</div>
                    <div className="text-slate-500">{a.actor} · {a.role}{a.new_state ? ` · → ${a.new_state}` : ""}</div>
                    {a.rationale && <div className="text-slate-500">{a.rationale}</div>}
                    <div className="font-mono text-[9px] text-slate-300">hash {a.event_hash?.slice(0, 16)}…</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="shadow">
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-indigo-300 bg-indigo-50 px-4 py-2.5 text-xs font-semibold text-indigo-800" data-testid="shadow-disclaimer">
            <Eye className="mt-0.5 h-4 w-4 shrink-0" /> {shadowLabel || "SHADOW MODE — Recommendation recorded for validation only. Existing operational workflow remains authoritative."}
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-4" data-testid="shadow-capture">
              <div className="mb-3 text-sm font-semibold text-slate-800">Recommendation vs Human Decision</div>
              <div className="mb-3 flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 p-2.5">
                <span className="text-xs text-slate-500">RequiSure recommendation (read-only)</span>
                <div className="flex items-center gap-2"><ColorDot color={color} label={color} /><StatusBadge status={o.risk_flag ? "RED" : o.status} size="sm" /></div>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Human operational decision</Label>
                  <Select value={shadow.human_decision} onValueChange={(v) => setShadow({ ...shadow, human_decision: v })}>
                    <SelectTrigger className="mt-1 h-8 text-xs" data-testid="shadow-human-decision"><SelectValue placeholder="Select the actual decision taken" /></SelectTrigger>
                    <SelectContent>{["Proceeded with testing", "Held for documentation", "Requested clarification", "Routed to review", "Cancelled order", "Other"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Agreement</Label>
                  <Select value={shadow.agreement} onValueChange={(v) => setShadow({ ...shadow, agreement: v })}>
                    <SelectTrigger className="mt-1 h-8 text-xs" data-testid="shadow-agreement"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Pending", "Agreement", "Disagreement", "Partial"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Claim outcome (optional · synthetic)</Label>
                  <Input className="mt-1 h-8 text-xs" data-testid="shadow-claim-outcome" placeholder="e.g. Paid / Denied — synthetic" value={shadow.claim_outcome} onChange={(e) => setShadow({ ...shadow, claim_outcome: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Reviewer notes</Label>
                  <Textarea className="mt-1 text-xs" rows={2} data-testid="shadow-notes" value={shadow.reviewer_notes} onChange={(e) => setShadow({ ...shadow, reviewer_notes: e.target.value })} />
                </div>
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="shadow-record" disabled={busy} onClick={recordShadow}><Eye className="mr-1.5 h-4 w-4" /> Record Observation</Button>
              </div>
              <div className="mt-3 rounded bg-slate-50 p-2 text-[11px] text-slate-500">Shadow Mode never holds, releases, alters, or transmits a real order. The existing human workflow remains authoritative; only aggregate validation metrics flow to Pilot Measurement.</div>
            </Card>
            <Card className="p-4" data-testid="shadow-log">
              <div className="mb-3 text-sm font-semibold text-slate-800">Observations Recorded (this session)</div>
              {shadowLog.length === 0 ? <div className="text-sm text-slate-500">No observations recorded yet.</div>
                : shadowLog.map((obs) => (
                  <div key={obs.id} className="mb-2 rounded-md border border-slate-200 p-2.5 text-xs" data-testid={`shadow-obs-${obs.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-slate-800">Rec: {obs.requisure_recommendation} · {obs.agreement}</span>
                      <span className="text-slate-400">{new Date(obs.created_at).toLocaleTimeString()}</span>
                    </div>
                    <div className="text-slate-600">Human: {obs.human_decision || "—"}{obs.claim_outcome ? ` · Outcome: ${obs.claim_outcome}` : ""}</div>
                    {obs.reviewer_notes && <div className="text-slate-500">{obs.reviewer_notes}</div>}
                    <div className="text-slate-400">by {obs.recorded_by}</div>
                  </div>
                ))}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="replay">
          <Card className="p-4" data-testid="decision-replay">
            <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800"><Rewind className="h-4 w-4" /> Decision Replay — Immutable Evaluation History</div>
            <p className="mb-3 text-xs text-slate-500">{replay?.note || "Each snapshot preserves the exact document version, evidence, policy/rule version, readiness result, review and override as they existed at the time. Snapshots are never recomputed with newer rules."}</p>
            {(replay?.snapshots || []).length === 0 ? <div className="text-sm text-slate-500">No historical evaluations to replay yet.</div>
              : <div className="space-y-3">
                {replay.snapshots.map((s) => (
                  <div key={s.evaluation_id} className="rounded-md border border-slate-200 p-3" data-testid={`replay-snapshot-${s.sequence}`}>
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <span className="font-display text-sm font-bold text-slate-900">Snapshot #{s.sequence}</span>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={s.readiness_result} size="sm" />
                        <ColorDot color={s.readiness_color} label={s.readiness_color} />
                        <span className="font-mono text-[10px] text-slate-400">{s.timestamp ? new Date(s.timestamp).toLocaleString() : "—"}</span>
                      </div>
                    </div>
                    <dl className="grid gap-x-8 gap-y-1 text-xs text-slate-600 sm:grid-cols-2">
                      <Row k="Document" v={`v${s.document?.version ?? "—"} — ${s.document?.label || ""}`} />
                      <Row k="Policy / rule version" v={s.policy_version || "—"} />
                      <Row k="Policy effective" v={s.policy_effective_date || "—"} />
                      <Row k="Summary" v={s.summary || "—"} />
                    </dl>
                    {(s.evidence_snapshot || []).length > 0 && (
                      <div className="mt-2">
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Evidence snapshot (as extracted then)</div>
                        {s.evidence_snapshot.map((f, i) => (
                          <div key={i} className="text-[11px] text-slate-600">• {FACT_LABELS[f.fact_type] || f.fact_type}: “{f.source_quote}” → {f.normalized_value} (conf {Math.round((f.confidence || 0) * 100)}%)</div>
                        ))}
                      </div>
                    )}
                    {(s.criterion_results || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {s.criterion_results.map((c) => <span key={c.criterion_code} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">{c.criterion_code}: {c.status}</span>)}
                      </div>
                    )}
                    {(s.reviews || []).map((r) => <div key={r.id} className="mt-1 text-[11px] text-slate-500">Review: {r.state}{r.resolution ? ` · ${r.resolution}` : ""}</div>)}
                    {(s.overrides || []).map((ov) => <div key={ov.id} className="mt-1 text-[11px] text-slate-500">Override: {ov.original_status} → {ov.new_status} · {ov.reviewer} (original retained)</div>)}
                  </div>
                ))}
              </div>}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <Dialog open={clarOpen} onOpenChange={setClarOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request Clarification</DialogTitle>
            <DialogDescription>Neutral, evidence-based request. Never supplies the desired clinical answer.</DialogDescription></DialogHeader>
          <Label className="text-xs">Message (optional — a neutral default is used if blank)</Label>
          <Textarea data-testid="clar-message" rows={4} value={clarMsg} onChange={(e) => setClarMsg(e.target.value)}
            placeholder="The submitted documentation does not contain an explicit symptom duration. Please review the existing patient record and provide any available documentation relevant to symptom duration." />
          <DialogFooter><Button data-testid="clar-submit" className="bg-teal-600 hover:bg-teal-700" disabled={busy} onClick={submitClar}>Send Request</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={docOpen} onOpenChange={setDocOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Supplemental Documentation</DialogTitle>
            <DialogDescription>Creates a new document version (originals are never overwritten) and re-evaluates.</DialogDescription></DialogHeader>
          <Textarea data-testid="doc-text" rows={5} value={docText} onChange={(e) => setDocText(e.target.value)}
            placeholder="e.g. Addendum: Symptom duration documented as four days per prior visit note." />
          <DialogFooter><Button data-testid="doc-submit" className="bg-teal-600 hover:bg-teal-700" disabled={busy} onClick={submitDoc}>Add & Re-evaluate</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Route to Human Review</DialogTitle></DialogHeader>
          <Label className="text-xs">Reason</Label>
          <Textarea data-testid="review-reason" rows={3} value={reviewReason} onChange={(e) => setReviewReason(e.target.value)} />
          <DialogFooter><Button data-testid="review-submit" disabled={busy} onClick={submitReview}>Route to Review</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4"><dt className="text-slate-400">{k}</dt><dd className="font-medium text-slate-700">{v || "—"}</dd></div>
);

const OWNER_BY_STATUS = {
  READY: "Lab / Billing operations",
  ACTION_NEEDED: "Ordering provider / intake",
  REVIEW_REQUIRED: "Revenue / Compliance reviewer",
};

const WORKFLOW_LABELS = {
  CLARIFICATION_REQUESTED: "Clarification Requested",
  UNDER_REVIEW: "Under Review",
  RESUBMITTED: "Resubmitted",
  SUBMITTED: "Submitted",
};

function reconPill(status) {
  const map = {
    "Supported": "bg-emerald-50 text-emerald-700 border-emerald-300",
    "Not Found in Available Record": "bg-amber-50 text-amber-800 border-amber-300",
    "Conflicting": "bg-rose-50 text-rose-800 border-rose-300",
    "Requires Review": "bg-slate-100 text-slate-600 border-slate-300",
  };
  return `inline-block rounded border px-2 py-0.5 text-[10px] font-semibold ${map[status] || "bg-slate-100 text-slate-600 border-slate-300"}`;
}

function TestingHistory({ th }) {
  if (!th || th.integrated === false) {
    return (
      <div className="flex items-start gap-2" data-testid="testing-history-not-integrated">
        <Minus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
        <span>Prior-testing history source is <strong>not integrated</strong> in this demonstration — RequiSure cannot state whether prior testing exists. <span className="text-slate-400">(Future integration)</span></span>
      </div>
    );
  }
  if (th.available === false || !th.count) {
    return (
      <div data-testid="testing-history-none-found">
        <div className="flex items-start gap-2">
          <CircleDot className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
          <span><strong>No prior testing found in the available records.</strong> This reflects the records RequiSure could access — it is not confirmation that none exists elsewhere.</span>
        </div>
        <div className="mt-2 text-[11px] text-slate-400">Frequency evaluation: {th.frequency_eval || "Unable to determine"}</div>
      </div>
    );
  }
  return (
    <div data-testid="testing-history-records">
      <div className="mb-2">Prior tests identified in the available longitudinal record: <strong>{th.count}</strong></div>
      <ul className="mb-2 space-y-1">
        {(th.tests || []).map((t, i) => (
          <li key={i} className="flex justify-between border-b border-slate-100 pb-1"><span>{t.panel}</span><span className="font-mono text-slate-500">{t.date}</span></li>
        ))}
      </ul>
      <div className="text-[11px] text-slate-400">Last relevant test: {th.last_date || "—"} · Source: {th.source || "—"}</div>
      <div className="text-[11px] text-slate-400">Frequency evaluation: {th.frequency_eval || "Awaiting Authoritative Rule"}</div>
    </div>
  );
}

function SourceCoverage({ sc }) {
  if (!sc) return <div className="text-slate-400">Source coverage not recorded for this order.</div>;
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Evaluated / available</div>
        <ul className="space-y-1">{(sc.evaluated || []).map((x, i) => (
          <li key={i} className="flex items-start gap-1.5"><CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-emerald-500" />{x}</li>
        ))}</ul>
      </div>
      <div>
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Not available / not integrated</div>
        <ul className="space-y-1">{(sc.not_available || []).map((x, i) => (
          <li key={i} className="flex items-start gap-1.5"><Minus className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />{x}</li>
        ))}</ul>
      </div>
    </div>
  );
}

function StageKicker({ n, title, rail }) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <span className="flex h-5 w-5 items-center justify-center rounded bg-slate-900 font-display text-[10px] font-bold text-white">{n}</span>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-700">{title}</span>
      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-400">{rail}</span>
    </div>
  );
}
